/**
 * KMD diagnostic message catalog — every user-facing compiler message in
 * English and Arabic (KHIYARSA-FIX-1 C1). diag() renders the `en` form into
 * KmdDiagnostic.message (the stable wire text); renderers re-render by UI
 * locale via the stored msgKey + params.
 *
 * Rules:
 *  - KMD syntax tokens (operators, kinds, keys, code snippets) stay ASCII in
 *    both languages — they are the language's own vocabulary, not prose.
 *  - Keys are `<area>.<variant>`; one key per distinct sentence pattern.
 *  - Adding a diag() call site means adding (or reusing) a catalog key —
 *    localization.test.ts fails on any diagnostic without a key.
 */

export type MsgParams = Record<string, string | number>;

type Entry = {
  en: (p: MsgParams) => string;
  ar: (p: MsgParams) => string;
};

export const MESSAGES = {
  // ---- frontmatter ---------------------------------------------------------
  "fm.missing": {
    en: () => `not a KMD document: missing frontmatter with "khiyarsa: 1"`,
    ar: () => `هذا ليس ملف خيارسة ماركداون: لا توجد مقدمة YAML تحتوي "khiyarsa: 1"`,
  },
  "fm.badYaml": {
    en: (p) => `invalid YAML frontmatter: ${p.error}`,
    ar: (p) => `مقدمة YAML غير صالحة: ${p.error}`,
  },
  "fm.notMapping": {
    en: () => `frontmatter must be a YAML mapping`,
    ar: () => `يجب أن تكون المقدمة خريطة YAML (مفتاح: قيمة)`,
  },
  "fm.versionTooNew": {
    en: (p) =>
      `this engine supports khiyarsa major version ${p.supported}; the file declares ${p.version}`,
    ar: (p) =>
      `هذا الإصدار من المحرك يدعم khiyarsa بإصدار ${p.supported}؛ بينما الملف يعلن الإصدار ${p.version}`,
  },
  "fm.versionMissing": {
    en: () => `missing or invalid "khiyarsa" version key`,
    ar: () => `مفتاح الإصدار "khiyarsa" مفقود أو غير صالح`,
  },
  "fm.unknownEnum": {
    en: (p) => `unknown ${p.key} "${p.value}"`,
    ar: (p) => `قيمة غير معروفة للمفتاح ${p.key}: "${p.value}"`,
  },
  "fm.noType": {
    en: () => `no "type" in frontmatter; defaulting to flow`,
    ar: () => `لا يوجد مفتاح "type" في المقدمة؛ سيُستخدم flow افتراضيًا`,
  },
  "fm.unknownKey": {
    en: (p) => `unknown frontmatter key "${p.key}" ignored`,
    ar: (p) => `تم تجاهل مفتاح غير معروف في المقدمة: "${p.key}"`,
  },
  "fm.titleConflict": {
    en: (p) => `both frontmatter "title" and "# ${p.label}" present; frontmatter wins`,
    ar: (p) => `يوجد عنوان في المقدمة وعنوان "# ${p.label}" معًا؛ عنوان المقدمة هو المعتمد`,
  },
  "family.unsupported": {
    en: (p) =>
      `family "${p.family}" is defined by the KMD spec but not yet supported by this engine version`,
    ar: (p) =>
      `النوع "${p.family}" معرّف في مواصفة KMD لكنه غير مدعوم بعد في هذا الإصدار من المحرك`,
  },

  // ---- styles frontmatter --------------------------------------------------
  "styles.notMapping": {
    en: () => `"styles" must be a mapping of class → properties`,
    ar: () => `يجب أن يكون "styles" خريطة من اسم الفئة إلى خصائصها`,
  },
  "styles.classNotMapping": {
    en: (p) => `style class "${p.className}" must be a mapping`,
    ar: (p) => `يجب أن تكون فئة التنسيق "${p.className}" خريطة خصائص`,
  },

  // ---- ids and references --------------------------------------------------
  "id.duplicateExplicit": {
    en: (p) => `duplicate explicit id "#${p.id}"`,
    ar: (p) => `المعرّف الصريح "#${p.id}" مكرر`,
  },
  "id.duplicateImplicit": {
    en: (p) => `duplicate label "${p.label}": implicit id becomes "${p.id}"`,
    ar: (p) => `التسمية "${p.label}" مكررة: المعرّف الضمني أصبح "${p.id}"`,
  },
  "ref.unresolved": {
    en: (p) => `unresolved reference "${p.ref}"`,
    ar: (p) => `مرجع غير معروف: "${p.ref}"`,
  },
  "ref.ambiguous": {
    en: (p) => `ambiguous reference "${p.ref}"`,
    ar: (p) => `المرجع "${p.ref}" غامض (أكثر من عنصر بنفس التسمية)`,
  },
  "seq.unknownParticipant": {
    en: (p) => `unknown participant "${p.ref}"`,
    ar: (p) => `مشارك غير معروف: "${p.ref}"`,
  },
  "ref.declarationHash": {
    en: (p) =>
      `reference "${p.ref}" written with "#" — "#" is declaration-only; write "${p.stripped}"`,
    ar: (p) =>
      `المرجع "${p.ref}" مكتوب بعلامة "#" — علامة "#" للتعريف فقط؛ اكتب "${p.stripped}"`,
  },

  // ---- attribute blocks ----------------------------------------------------
  "attrs.unclosedQuote": {
    en: () => `malformed attribute block: unclosed quote`,
    ar: () => `كتلة سمات غير سليمة: علامة اقتباس غير مغلقة`,
  },
  "attrs.multipleIds": {
    en: () => `malformed attribute block: more than one #id`,
    ar: () => `كتلة سمات غير سليمة: أكثر من معرّف #id واحد`,
  },
  "attrs.conflictingKinds": {
    en: (p) => `conflicting kinds "${p.a}" and "${p.b}"`,
    ar: (p) => `تعارض بين النوعين "${p.a}" و "${p.b}"`,
  },
  "attrs.unknownFieldFlag": {
    en: (p) => `unknown field flag "${p.word}"`,
    ar: (p) => `علامة حقل غير معروفة: "${p.word}"`,
  },
  "attrs.invalidCardinality": {
    en: (p) => `invalid cardinality "${p.word}"`,
    ar: (p) => `تعددية علاقة غير صالحة: "${p.word}"`,
  },
  "attrs.unknownBareword": {
    en: (p) => `unknown attribute "${p.word}"`,
    ar: (p) => `سمة غير معروفة: "${p.word}"`,
  },
  "attrs.invalidArrowhead": {
    en: (p) => `invalid arrowhead "${p.value}" for "${p.key}="`,
    ar: (p) => `رأس سهم غير صالح "${p.value}" للمفتاح "${p.key}="`,
  },
  "attrs.directionReserved": {
    en: () => `per-container "direction=" is not supported by this engine version`,
    ar: () => `المفتاح "direction=" على مستوى الحاوية غير مدعوم في هذا الإصدار من المحرك`,
  },
  "attrs.unknownKey": {
    en: (p) => `unknown attribute key "${p.key}"`,
    ar: (p) => `مفتاح سمة غير معروف: "${p.key}"`,
  },
  "attrs.invalidValueForKey": {
    en: (p) => `invalid value "${p.value}" for "${p.key}"`,
    ar: (p) => `قيمة غير صالحة "${p.value}" للمفتاح "${p.key}"`,
  },
  "attrs.invalidColor": {
    en: (p) => `invalid color token "${p.value}" for "${p.key}"`,
    ar: (p) => `رمز لون غير صالح "${p.value}" للمفتاح "${p.key}"`,
  },
  "attrs.invalidOpacity": {
    en: (p) => `invalid opacity "${p.value}"`,
    ar: (p) => `قيمة شفافية غير صالحة: "${p.value}"`,
  },
  "attrs.opacityClamped": {
    en: (p) => `opacity ${p.n} out of range 0–100; clamped to ${p.clamped}`,
    ar: (p) => `الشفافية ${p.n} خارج النطاق 0–100؛ تم ضبطها إلى ${p.clamped}`,
  },

  // ---- edges and prose -----------------------------------------------------
  "edge.unknownOperator": {
    en: (p) => `unknown operator "${p.op}"`,
    ar: (p) => `عامل ربط غير معروف: "${p.op}"`,
  },
  "edge.duplicateCollapsed": {
    en: (p) => `duplicate edge collapsed: ${p.from} -> ${p.to}`,
    ar: (p) => `تم دمج علاقة مكررة: ${p.from} -> ${p.to}`,
  },
  "prose.notEdge": {
    en: () => `paragraph is not a valid edge statement; ignored`,
    ar: () => `الفقرة ليست جملة علاقة صالحة؛ تم تجاهلها`,
  },
  "prose.codeBlock": {
    en: () => `code block ignored`,
    ar: () => `تم تجاهل كتلة الشيفرة`,
  },
  "prose.tableIgnored": {
    en: () => `table ignored (tables are only meaningful in the er family)`,
    ar: () => `تم تجاهل الجدول (الجداول ذات معنى في نوع er فقط)`,
  },
  "prose.emptyItem": {
    en: () => `empty list item ignored`,
    ar: () => `تم تجاهل عنصر قائمة فارغ`,
  },
  "prose.seqListItems": {
    en: () => `list items have no meaning in the sequence family; messages are edge statements`,
    ar: () => `عناصر القائمة بلا معنى في نوع sequence؛ الرسائل تُكتب كجمل علاقات`,
  },
  "prose.timelineFlatForm": {
    en: () => `timeline items outside "##" periods must be "- when: what"`,
    ar: () => `عناصر timeline خارج فترات "##" يجب أن تكون بصيغة "- متى: ماذا"`,
  },

  // ---- directives, headings, structure ------------------------------------
  "directive.wrongFamily": {
    en: (p) => `directive ":::${p.name}" is not valid in the ${p.family} family`,
    ar: (p) => `التوجيه ":::${p.name}" غير صالح في النوع ${p.family}`,
  },
  "heading.secondH1": {
    en: () => `only one "#" title heading is allowed`,
    ar: () => `يُسمح بعنوان "#" واحد فقط`,
  },
  "heading.tooDeep": {
    en: (p) => `"${p.heading}": the ${p.family} family does not nest containers this deep`,
    ar: (p) => `"${p.heading}": النوع ${p.family} لا يدعم تداخل الحاويات إلى هذا العمق`,
  },
  "heading.subsystemNoZone": {
    en: (p) => `"### ${p.label}" has no enclosing "##" zone`,
    ar: (p) => `"### ${p.label}" ليس داخل أي منطقة "##"`,
  },
  "er.fieldOutsideEntity": {
    en: () => `field outside an "##" entity`,
    ar: () => `حقل خارج أي كيان "##"`,
  },
  "er.tableOutsideEntity": {
    en: () => `table outside an "##" entity`,
    ar: () => `جدول خارج أي كيان "##"`,
  },
  "hr.noMeaning": {
    en: (p) => `"---" has no meaning in the ${p.family} family`,
    ar: (p) => `"---" بلا معنى في النوع ${p.family}`,
  },

  // ---- section breaks ------------------------------------------------------
  "sb.beforeAnySection": {
    en: () => `"---" before any section; nothing to connect`,
    ar: () => `"---" قبل أي قسم؛ لا يوجد ما يمكن وصله`,
  },
  "sb.connected": {
    en: (p) => `section break connected: ${p.list}`,
    ar: (p) => `فاصل الأقسام أنشأ الوصلات: ${p.list}`,
  },
  "sb.nothing": {
    en: () => `section break connected nothing: every node before it already has an outgoing edge`,
    ar: () => `فاصل الأقسام لم يصل شيئًا: كل عنصر قبله له علاقة صادرة بالفعل`,
  },
  "sb.fanout": {
    en: (p) => `section break fans out to ${p.count} nodes: ${p.list}`,
    ar: (p) => `فاصل الأقسام يتفرع إلى ${p.count} عناصر: ${p.list}`,
  },

  // ---- limits and layout ---------------------------------------------------
  "limit.fileBytes": {
    en: (p) => `document exceeds ${p.kb} kB limit`,
    ar: (p) => `الملف يتجاوز الحد الأقصى ${p.kb} كيلوبايت`,
  },
  "limit.nodes": {
    en: (p) => `node limit exceeded (${p.max})`,
    ar: (p) => `تم تجاوز الحد الأقصى لعدد العناصر (${p.max})`,
  },
  "limit.edges": {
    en: (p) => `edge limit exceeded (${p.max})`,
    ar: (p) => `تم تجاوز الحد الأقصى لعدد العلاقات (${p.max})`,
  },
  "limit.labelChars": {
    en: (p) => `label exceeds ${p.max} characters`,
    ar: (p) => `التسمية تتجاوز ${p.max} حرفًا`,
  },
  "limit.styleClasses": {
    en: (p) => `too many style classes (${p.count} > ${p.max})`,
    ar: (p) => `عدد فئات التنسيق كبير جدًا (${p.count} > ${p.max})`,
  },
  "layout.failed": {
    en: () => `the diagram could not be laid out; try spacing: spacious or fewer nodes`,
    ar: () => `تعذّر توزيع المخطط؛ جرّب spacing: spacious أو قلّل عدد العناصر`,
  },

  // ---- images, icons, tags -------------------------------------------------
  "image.badScheme": {
    en: () => `image source violates security policy (data: or https: only)`,
    ar: () => `مصدر الصورة يخالف سياسة الأمان (يُسمح بـ data: أو https: فقط)`,
  },
  "image.remoteSkipped": {
    en: () => `remote image not fetched by this engine version; skipped`,
    ar: () => `هذا الإصدار من المحرك لا يجلب الصور البعيدة؛ تم تخطيها`,
  },
  "icon.notFound": {
    en: (p) => `icon "${p.icon}" not found; rendered without icon`,
    ar: (p) => `الأيقونة "${p.icon}" غير موجودة؛ سيُرسم العنصر بدون أيقونة`,
  },
  "tag.unknown": {
    en: (p) => `unknown tag ".${p.tag}" ignored`,
    ar: (p) => `تم تجاهل وسم غير معروف: ".${p.tag}"`,
  },

  // ---- normalizer ----------------------------------------------------------
  "norm.bom": {
    en: () => `normalizer: stripped BOM`,
    ar: () => `المُطبِّع: تمت إزالة علامة BOM`,
  },
  "norm.repair": {
    en: (p) => `normalizer: ${p.rule}`,
    ar: (p) => `المُطبِّع: ${p.ruleAr ?? p.rule}`,
  },
  "mermaid.subgraph": {
    en: () =>
      `this looks like Mermaid "subgraph" — KMD groups with "## Heading" (or :::group)`,
    ar: () =>
      `يبدو هذا كصيغة "subgraph" من Mermaid — في KMD استخدم "## عنوان" (أو :::group) للتجميع`,
  },
  "mermaid.end": {
    en: () =>
      `this looks like a Mermaid "end" line — KMD containers end at the next heading; delete this line`,
    ar: () =>
      `يبدو هذا كسطر "end" من Mermaid — حاويات KMD تنتهي عند العنوان التالي؛ احذف هذا السطر`,
  },
  "mermaid.pipes": {
    en: () =>
      `this looks like Mermaid "|label|" syntax — KMD writes the label after a colon: A -> B: label`,
    ar: () =>
      `يبدو هذا كصيغة "|تسمية|" من Mermaid — في KMD تُكتب التسمية بعد نقطتين: A -> B: تسمية`,
  },
  "mermaid.brackets": {
    en: () =>
      `this looks like Mermaid "A[label]" shape brackets — in KMD the item text IS the label: "- label {kind}"`,
    ar: () =>
      `يبدو هذا كأقواس أشكال "A[تسمية]" من Mermaid — في KMD نص العنصر هو التسمية نفسها: "- تسمية {نوع}"`,
  },
  "note.lazySwallow": {
    en: (p) =>
      `the note swallowed ${p.count} following line(s) — add a blank line after the ">" note`,
    ar: (p) =>
      `ابتلعت الملاحظة ${p.count} من الأسطر التالية — أضف سطرًا فارغًا بعد ملاحظة ">"`,
  },
  "mermaid.braces": {
    en: () =>
      `this looks like Mermaid "A{label}" shape braces — KMD attribute blocks are written with a space: "Label {attrs}"`,
    ar: () =>
      `يبدو هذا كأقواس أشكال "A{تسمية}" من Mermaid — كتل السمات في KMD تُكتب بمسافة قبلها: "تسمية {سمات}"`,
  },
} as const satisfies Record<string, Entry>;

export type MsgKey = keyof typeof MESSAGES;

/** Locale-fixed scaffolding used by renderers (formatDiagnostic, dialog). */
export const SCAFFOLD = {
  en: {
    severity: { error: "error", warning: "warning", info: "info" } as const,
    expected: "expected",
    fix: "fix",
    strictMode: "(strict mode)",
  },
  ar: {
    severity: { error: "خطأ", warning: "تحذير", info: "معلومة" } as const,
    expected: "المتوقع",
    fix: "الإصلاح",
    strictMode: "(الوضع الصارم)",
  },
} as const;
